import type { BrokerAccount } from '@/lib/types'

/**
 * Bandeau de compte, en haut de l'écran (§8).
 *
 * Affiche les valeurs **réelles** du broker quand il est connecté. Sans
 * connexion, il indique explicitement que les chiffres viennent de la saisie
 * manuelle : confondre un capital saisi avec un solde réel serait la pire
 * confusion possible dans cette application.
 */
export function AccountBar({
  account,
  live,
  fallbackCurrency,
  fallbackBalance,
}: {
  account: BrokerAccount | null
  /** Vrai si les chiffres viennent du broker. */
  live: boolean
  fallbackCurrency: string
  fallbackBalance: string
}) {
  const currency = account?.currency ?? fallbackCurrency
  const balance = account?.balance ?? fallbackBalance
  const equity = account?.equity ?? fallbackBalance
  const freeMargin = account?.free_margin ?? fallbackBalance

  return (
    <div className="grid grid-cols-3 divide-x divide-border border-b border-border bg-panel">
      <Cell label="Solde" value={`${format(balance)} ${currency}`} live={live} />
      <Cell label="Équité" value={`${format(equity)} ${currency}`} live={live} />
      <Cell label="Marge libre" value={`${format(freeMargin)} ${currency}`} live={live} />
    </div>
  )
}

/*
 * Libellé et montant sur UNE ligne dès qu'il y a la place.
 *
 * Empilés, les trois chiffres du compte coûtaient une bande de cinquante
 * pixels en haut de l'écran principal — soit autant de bougies en moins, pour
 * trois nombres qu'on surveille du coin de l'œil. Sur téléphone, où la largeur
 * manque, ils restent empilés.
 */
function Cell({ label, value, live }: { label: string; value: string; live: boolean }) {
  return (
    <div className="px-3 py-1.5 md:flex md:items-baseline md:gap-2 md:py-1">
      <p className="text-[10px] uppercase tracking-wide text-ink-muted">
        {label}
        {!live && <span className="ml-1 text-warn">saisi</span>}
      </p>
      <p className={`num text-[13px] ${live ? 'text-ink' : 'text-ink-muted'}`}>{value}</p>
    </div>
  )
}

/** Arrondit à deux décimales sans passer par un flottant imprécis à l'affichage. */
function format(value: string): string {
  const parsed = Number(value)
  return Number.isFinite(parsed)
    ? parsed.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : value
}
